import { categoricalTokens, tagTokens } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// What a tag is painted.
//
// A tag is a thing that differs without ranking, which is exactly what the
// categorical palette is for: `color.categorical` in the token source names
// tags first among its consumers. The colour is a *stored value* rather than a
// style, because the column holds one string that both schemes paint, so it is
// seeded from the palette rather than declared: this is the only place in the
// package that had to reach for `eslint-disable no-restricted-syntax` to write
// a colour, and it no longer does.
//
// The light-scheme `bg` is the one stored. The dark entries are translucent
// overlays, which mean nothing written into a database column and read back on
// a light surface.
//
// **A tag is drawn from its pastel, never from the stored string.** Chips used
// to paint the stored colour with black text over it, and the colour came from
// a free picker, so production held `#187589`, `#5343bb` and `#0c21e2`: chips
// whose names could not be read. The stored value now only says *which*
// pastel; the chip takes that pastel's `bg` and `text` pair for the scheme it
// is in, which is readable in both. A value from before the picker was held to
// the palette still draws as a pastel, picked by the tag's id.
//
// **Which pastels, and in what order, is `color.tag` in the token source.**
// The backend picks the colour of a tag created without one from the same
// list, generated into it, so a value it stores is always one this module
// reads back as the pastel it meant.
// ----------------------------------------------------------------------

export const TAG_PASTELS = tagTokens.pastels

export type TagPastel = (typeof TAG_PASTELS)[number]

export const TAG_COLORS: readonly string[] = TAG_PASTELS.map(
  (name) => categoricalTokens.light[name].bg,
)

/** The stored value for a pastel, which is its light-scheme `bg`. */
export const tagColorOf = (pastel: TagPastel): string =>
  categoricalTokens.light[pastel].bg

/**
 * The pastel a tag is drawn in.
 *
 * Its stored colour, when that is one of the palette's. Anything else is a
 * colour from before tags were held to the palette, or one written by
 * something other than the console, and it is drawn as the pastel its id
 * lands on: a readable chip whatever the column says, and the same one on
 * every screen that draws it.
 */
export const tagPastel = (tag: {
  id?: number | null
  color?: string | null
}): TagPastel => {
  const stored = tag.color?.toLowerCase()
  const index = TAG_COLORS.findIndex((color) => color.toLowerCase() === stored)
  if (index >= 0) {
    return TAG_PASTELS[index]
  }
  return TAG_PASTELS[Math.abs(tag.id ?? 0) % TAG_PASTELS.length]
}

/**
 * The colour a new tag is made in: the first pastel none of the provider's
 * tags is drawn in, and once all eight are taken, the one fewest of them wear
 * (the earlier in the palette on a tie).
 *
 * Counted by the pastel each tag is *drawn* in, not by the string it stores.
 * A tag from before the palette stores a colour no pastel matches and is drawn
 * as the pastel its id lands on, and counting only stored strings offered that
 * pastel as free: the form opened on a colour its own picker showed as taken.
 * Never random, so the same steps make the same fleet twice; and never by
 * position once the eight are taken, which repeated the first pastel however
 * many tags already wore it.
 *
 * The backend makes the same choice for a tag created without a colour
 * (`providerTagColorNext`), from the same list.
 */
export const nextTagColor = (
  tags: readonly { id?: number | null; color?: string | null }[],
): string => {
  const wearers = new Map<TagPastel, number>(
    TAG_PASTELS.map((pastel) => [pastel, 0]),
  )
  tags.forEach((tag) => {
    const pastel = tagPastel(tag)
    wearers.set(pastel, (wearers.get(pastel) ?? 0) + 1)
  })
  const fewest = TAG_PASTELS.reduce((best, pastel) =>
    (wearers.get(pastel) ?? 0) < (wearers.get(best) ?? 0) ? pastel : best,
  )
  return tagColorOf(fewest)
}

/**
 * The pastel a tag with nothing stored is drawn in, from its name.
 *
 * For a tag that is a word rather than a row: a rental's feedback tags are
 * what the renter picked in the app, with no colour and no id anywhere. It is
 * decided by the name and never by where the tag sits in a list, so the same
 * name lands on the same pastel on every row, every screen and every load.
 */
export const namedTagPastel = (name: string): TagPastel => {
  // A string hash kept under a prime, so it stays an exact integer however
  // long the name is.
  const hash = Array.from(name).reduce(
    (sum, char) => (sum * 31 + (char.codePointAt(0) ?? 0)) % 2147483647,
    0,
  )
  return TAG_PASTELS[hash % TAG_PASTELS.length]
}
